import { defineStore } from 'pinia';
import { StoreModuleName } from '../../constants';
import {
  ComputedExamInfo,
  ComputedExamine,
  ComputedProgress,
  ComputedAnswerItem,
  QuestionCollectStatus,
  ComputedExamQuestionItem,
  ComputedQuestionType,
  ExamAnswerPageMetaInfo,
} from './types';
import { QuestionTypeEnum } from '~/src/constants/exam/answerFlow';
import {
  DailyPunchTypeEnum,
  ExamDetailInfoResponse,
  ExamQuestionItem,
} from '@/src/api/modules/exam/types';
import { isString, snakeCase } from 'lodash-es';
import { CustomFetchContext } from '~/src/api/requests/types';
import {
  getQuestionType,
  getQuestionAnswer,
  getOptionValue,
  isDone,
  getChapterPracticeAnswer,
  getAnswer,
  getAnswerImgs,
  getAnswerText,
  getRichTextAnswer,
  setQuestionDisabled,
} from './utils';
import {
  fetchExamInfo,
  fetchExamInfoToken,
  fetchDailyPunchCardQuestion,
  fetchListExamInfo,
  fetchChapterExamInfo,
  saveExamAnswerProgress,
  saveChapterExamAnswerProgress,
  saveListExamAnswerProgress,
  submitExamAnswer,
  submitListExamAnswer,
  submitDailyPunchCardAnswer,
  submitChapterExamAnswer,
  addCollectQuestion,
  removeCollectQuestion,
  getExamTime,
  fetchKnowledgeExamInfo,
  saveKnowledgeExamAnswerProgress,
  submitKnowledgeExamAnswer,
} from '~/src/api/modules/exam';
import { deepConvertKey } from '~/src/utils/lodashExtend';
import { AnswerModeEnum, ExamModeEnum } from '~/src/constants/exam';
import { objectToFormData } from '@/src/utils';
import dayjs from 'dayjs';
import { generateExamResultUrl } from '~/src/utils/exam/url';
import { Router } from 'vue-router';
import { showLoadingToast, showSuccessToast } from 'vant';
import { GLOBAL_CONFIG } from '~/configs/environment/environment';
import { isSubjective } from '~/src/utils/exam';
import {
  fetchUserWrongDetail,
  getUserWrongProgress,
  saveUserWrongProgress,
  submitUserWrong,
} from '~/src/api/modules/user-center/wrong';
import {
  saveCollectionProgress,
  getCollectionProgress,
  submitUserCorrection,
} from '~/src/api/modules/user-center/collection';
import { UserWrongAnswerItem } from '~/src/api/modules/user-center/wrong/types';
import { fetchCollectionList } from '~/src/api/modules/user-center/collection';
import { UserCollectionAnswerItem } from '~/src/api/modules/user-center/collection/types';
import { useUserStore } from '../user';

export const useExamAnswerStore = defineStore(StoreModuleName.EXAM_ANSWER, () => {
  const userStore = useUserStore();
  // 原始数据非响应式
  let rawExamInfo = {} as ExamDetailInfoResponse;
  // 考试模式
  const examMode = ref<ExamModeEnum>(1);
  // 作答模式
  const answerMode = ref<AnswerModeEnum>(1);

  // 不展示我的答案，如背题模式
  const needlessAnswer = computed(() => {
    return answerMode.value === AnswerModeEnum.RecitationMode;
  });

  const createInitPageMeta = (): ExamAnswerPageMetaInfo => {
    return {
      id: '',
      version: '',
      tradeNumber: '',
      exchangeType: '',
      codeId: '',
      isCert: '',
      type: '',
      userSubject: userStore.userMeta.subjectId,
      highFrequency: '',
      today: '',
      date: dayjs().format('YYYY-MM-DD'),
    };
  };

  const pageMeta = ref(createInitPageMeta());

  const resetPageMeta = () => {
    pageMeta.value = createInitPageMeta();
  };

  const createInitExamInfo = (): ComputedExamInfo => {
    return {
      questionList: [],
      examine: {
        id: 0,
        title: '',
        lenTime: 0,
        totalQuestion: 0,
        totalScore: 0,
        submitLimitNum: '',
        sheetName: '',
        machineType: '',
        isOnce: false,
        isGtOne: false,
        alreadyAnswersNum: '',
        answersNum: 0,
        examTime: 0,
      },
      progress: {
        answer: [],
        index: '',
        markQuestionIds: [],
        qid: '',
        size: '',
        time: '0',
      },
    };
  };

  // 考试信息
  const computedExamInfo = ref(createInitExamInfo());

  const resetExamInfo = () => {
    computedExamInfo.value = createInitExamInfo();
  };

  // 考试提示
  const examStartTips = computed(() => {
    const tipList = [
      '在规定时间内答题；',
      '交卷后会进行系统判分，请您耐心等待（系统暂不支持主观题判分，请您考试后参考答案解析）；',
      '交卷时答题统计不包含主观题；',
    ];

    if (computedExamInfo.value.examine.isOnce) {
      // 只考一次
      tipList.push('此试卷在规定时间范围内，只能提交一次哦，您准备好了吗？');
    } else if (computedExamInfo.value.examine.isGtOne) {
      tipList.push(
        `你已提交${computedExamInfo.value.examine.alreadyAnswersNum}次作答，该试卷还可提交${computedExamInfo.value.examine.answersNum}次作答。`
      );
      tipList.push('建议在得到批改后，再提交下次作答。');
    }

    return tipList;
  });
  // 计时结束的提示
  const examTimeEndTips = computed(() => {
    if (isCertExam.value) {
      return [
        '时间到，考试已结束',
        '您当前页面的作答已上传，并计算成绩',
        '考试已不可继续答题，不可重新考试',
      ];
    } else {
      return ['答题时间到啦', '已经答过的部分会自动提交', '谢谢您的参与'];
    }
  });
  // 考试设置
  const examSetting = ref({
    showSaveProgressBtn: true, // 展示保存进度按钮
    autoChangeQuestion: true, // 自动切题
    onePageQuestion: false, // 一页一题
    currentQuestionIndex: 0, // 当前题目索引
    showExamTips: true, // 是否展示考试提示
    showPauseButton: true, // 是否展示暂停按钮
    lastQuestionId: 0, // 上次做题
  });
  // 是否人才认证 DTC 1.无保存进度功能 2.不允许暂停 3.无考试提示 4.交卷文案 5.结果页用旧的模板
  const isCertExam = ref(false);
  const certExamInfo = ref({
    examId: '',
    tradeNumber: '',
    exchangeType: '',
    codeId: '',
  });

  // 已答的数量
  const answeredQuestionCount = computed(() => {
    return computedExamInfo.value.questionList.filter(
      item => item.answer && item.answer.length > 0 && item.answer[0]
    ).length;
  });
  // 已标记题目的ids
  const markedQuestionIds = computed(() => {
    return computedExamInfo.value.questionList.filter(item => item.isTagged).map(item => item.id);
  });
  // 考试答案
  const examAnswerList = computed(() => {
    return computedExamInfo.value.questionList.map(item => {
      return deepConvertKey(
        {
          answer: getAnswer(item.computedQuestionType, item.answer),
          answerImgs: getAnswerImgs(item.computedQuestionType, item.answer),
          answerText: getAnswerText(item.computedQuestionType, item.answer),
          answerType: item.answerType,
          done: isDone(item.answer),
          id: item.id,
          open: '',
          qindex: item.serialNumber,
          questionType: item.questionType,
          page: item.belongPage,
        },
        snakeCase
      );
    }) as unknown as ComputedAnswerItem[];
  });
  // 最后一道做过的题
  const lastAnsweredQuestion = computed(() => {
    let answeredQuestionIndex = 0;
    examAnswerList.value.forEach((i: ComputedAnswerItem, index: number) => {
      if (i.done) {
        answeredQuestionIndex = index;
      }
    });

    return examAnswerList.value[answeredQuestionIndex];
  });

  // 分页信息
  const pageInfo = ref({
    page: 1,
    pageSize: 200,
    total: 0,
    isLoaded: false,
  });

  // 处理试卷信息
  const computeRawExamInfo = (rawExamInfo: ExamDetailInfoResponse): ComputedExamInfo => {
    const questionList = rawExamInfo.data.question.map(quest => {
      return {
        id: quest.questionId,
        score: quest.score,
        serialNumber: quest.index,
        analyze: quest.analyze,
        options: isString(quest.option)
          ? []
          : quest.option?.map((item, index) => {
              return {
                label: item,
                value: getOptionValue(index),
              };
            }),
        // 是否已标记
        isTagged: false,
        questionType: quest.questionType,
        answerType: quest.answerType,
        computedQuestionType: getQuestionType(quest.questionType, quest.answerType), // 处理type
        answer: getQuestionAnswer(
          quest.userAnswer || quest.answer,
          examMode.value,
          getQuestionType(quest.questionType, quest.answerType)
        ),
        rightAnswer: quest.answer,
        questTypeName: quest.showTypeName || QuestionTypeEnum[quest.questionType],
        questionTitle: quest.questionTitle,
        sortSon: Number(quest.sortSon),
        materialText: quest.materialText,
        belongPage: quest.belongPage || 0,
        like: quest.like || 0,
        isCheck: Boolean(quest.userAnswer && quest.userAnswer[0]),
        disabled: setQuestionDisabled(
          examMode.value,
          answerMode.value,
          isSubjective(getQuestionType(quest.questionType, quest.answerType)),
          getQuestionAnswer(
            quest.userAnswer || quest.answer,
            examMode.value,
            getQuestionType(quest.questionType, quest.answerType)
          )
        ),
      };
    });

    const { isOnce, isGtOne, alreadyAnswersNum, answersNum } = rawExamInfo;

    const examine: ComputedExamine = {
      ...rawExamInfo.data.examine,
      isOnce,
      isGtOne,
      alreadyAnswersNum,
      answersNum,
      examTime: rawExamInfo.data.examine.lenTime * 60,
    };

    let progress: ComputedProgress = {
      answer: [],
      index: '',
      markQuestionIds: [],
      qid: '',
      size: '',
      time: '0',
    };

    // 处理进度
    if (
      rawExamInfo.data.progress &&
      isObject(rawExamInfo.data.progress) &&
      Object.keys(rawExamInfo.data.progress).length > 0
    ) {
      progress = rawExamInfo.data.progress;
      // 设置标记

      if (
        progress.markQuestionIds &&
        isArray(progress.markQuestionIds) &&
        progress.markQuestionIds.length > 0
      ) {
        questionList.forEach(i => {
          if (progress.markQuestionIds.includes(String(i.id))) {
            i.isTagged = true;
          }
        });
      }

      // 设置答案
      if (progress && progress.answer && progress.answer.length > 0) {
        progress.answer.forEach(i => {
          if (i.done) {
            const index = questionList.findIndex(questionItem => questionItem.id === i.id);

            if (index > -1) {
              if (
                [ComputedQuestionType.MaterialRichText, ComputedQuestionType.RichText].includes(
                  questionList[index].computedQuestionType
                )
              ) {
                questionList[index].answer = getRichTextAnswer(
                  i.answer,
                  i.answerImgs,
                  i.answerText
                );
              } else {
                questionList[index].answer = i.answer;
              }

              questionList[index].isCheck = true;

              questionList[index].disabled = setQuestionDisabled(
                examMode.value,
                answerMode.value,
                isSubjective(questionList[index].computedQuestionType),
                questionList[index].answer
              );
            }
          }
        });
      }

      // 设置最后做的一个题目
      if (rawExamInfo.data.progress?.questionId || rawExamInfo.data.progress?.qid) {
        rawExamInfo.data.progress.questionId &&
          (examSetting.value.lastQuestionId = +rawExamInfo.data.progress.questionId);
        rawExamInfo.data.progress.qid &&
          (examSetting.value.lastQuestionId = +rawExamInfo.data.progress.qid);
      }
    }

    return {
      questionList,
      examine,
      progress,
    } as ComputedExamInfo;
  };

  // 更新试卷详情
  const updateExamInfo = async (
    fetchContext: CustomFetchContext,
    query: {
      mode: ExamModeEnum;
      aMode: AnswerModeEnum | undefined;
      id: string;
      version?: string;
      tradeNumber?: string;
      exchangeType?: string;
      codeId?: string;
      isCert?: string;
      type?: string;
      highFrequency?: string;
      today?: string;
    }
  ) => {
    const {
      mode,
      aMode,
      id,
      version,
      tradeNumber,
      exchangeType,
      isCert,
      codeId,
      highFrequency,
      today,
    } = query;

    Object.assign(pageMeta.value, {
      id,
      version,
      tradeNumber,
      exchangeType,
      isCert,
      codeId,
      highFrequency,
      today,
    });
    const { userSubject } = pageMeta.value;

    examMode.value = mode;

    if (aMode) {
      answerMode.value = aMode;
    }

    if (isCert && isCert === '1') {
      isCertExam.value = true;
      examSetting.value.showExamTips = false;
      examSetting.value.showPauseButton = false;
      certExamInfo.value = {
        examId: id,
        tradeNumber: tradeNumber || '',
        exchangeType: exchangeType || '',
        codeId: codeId || '',
      };
    }

    // 如果是练习模式 自动切换成逐题模式
    if (answerMode.value === AnswerModeEnum.PracticeMode) {
      examSetting.value.onePageQuestion = true;
    } else {
      examSetting.value.onePageQuestion = false;
    }

    // 背题模式不需要开场弹窗
    if (answerMode.value === AnswerModeEnum.RecitationMode) {
      examSetting.value.showExamTips = false;
    } else {
      examSetting.value.showExamTips = true;
    }

    if (examMode.value === ExamModeEnum.NormalExam) {
      // 普通考试
      // 先获取token
      const examInfoTokenData = new FormData();
      examInfoTokenData.set('exam', '' + id);

      try {
        const { time, token } = (await fetchExamInfoToken(examInfoTokenData, {}, fetchContext))
          .data;
        // 再获取试卷详情
        const examInfoQuery: {
          id: string;
          tradeNumber?: string;
          exchangeType?: string;
          token: string;
          time?: string;
          version?: string;
          codeId?: string;
          needAnswer?: string;
        } = {
          id,
          tradeNumber,
          exchangeType,
          token,
          time,
          version,
          needAnswer: '1',
        };

        if (isCertExam.value) {
          examInfoQuery.codeId = codeId;
        }

        const res = await fetchExamInfo(examInfoQuery, {}, fetchContext);

        if (res.data) {
          try {
            rawExamInfo = deepConvertKey(JSON.parse(window.atob(res.data)));

            // 只考一次
            if (rawExamInfo.isOnce) {
              const examTimeRes = await getExamTime({ id, version });
              const onceTime = examTimeRes.data.onceTime;

              rawExamInfo.data.progress.time = onceTime + '';
              examSetting.value.showSaveProgressBtn = false;
            }

            computedExamInfo.value = computeRawExamInfo(rawExamInfo);
            console.log('computedExamInfo', computedExamInfo.value);
          } catch (error) {
            console.error(error);
          }
        }

        return res;
      } catch (error) {
        return Promise.reject(error);
      }
    } else if (
      [ExamModeEnum.NormalExam, ExamModeEnum.ImitateExam, ExamModeEnum.PreviousExam].includes(
        examMode.value
      )
    ) {
      // 练习模式需要answer回去正确答案回显出来
      const needAnswer = answerMode.value === AnswerModeEnum.PracticeMode ? 1 : 0;
      // 模拟考场 历年试题
      const res = await fetchListExamInfo({ id, needAnswer }, {}, fetchContext);

      if (res.data.data) {
        try {
          rawExamInfo = {
            data: {
              question: res.data.data.question,
              examine: {
                id: res.data.data.examine.id,
                title: res.data.data.examine.title,
                lenTime: Number(res.data.data.examine.lenTime.time),
                totalQuestion: res.data.data.examine.totalQuestion,
                totalScore: Number(res.data.data.examine.totalScore),
                submitLimitNum: '',
                sheetName: '',
                machineType: '',
              },
              progress: res.data.data.process.answer
                ? res.data.data.process
                : {
                    answer: [],
                    index: '',
                    markQuestionIds: [],
                    qid: '',
                    size: '',
                    time: '0',
                  },
            },
            eduUserName: '',
            alreadyAnswersNum: '',
            answersNum: 0,
            isGtOne: false,
            isOnce: false,
            userInfo: {
              mobile: '',
              realName: '',
              sex: '',
              userName: '',
            },
          };
          console.log(rawExamInfo);
          examSetting.value.lastQuestionId = 0;
          computedExamInfo.value = computeRawExamInfo(rawExamInfo);
        } catch (error) {
          console.error(error);
        }
      }

      return res;
    } else if (examMode.value === ExamModeEnum.PunchInDaily) {
      // 每日打卡
      examSetting.value.showExamTips = false; // 无需提示
      const res = await fetchDailyPunchCardQuestion(
        { type: query.type as DailyPunchTypeEnum, id, userSubject, date: pageMeta.value.date },
        {},
        fetchContext
      );

      if (res.data) {
        try {
          const question: ExamQuestionItem[] = [];
          res.data.forEach((item, index) => {
            question.push({
              index: index + 1,
              questionId: Number(item.questionId),
              questionTitle: item.questionTitle,
              questionType: Number(item.questionType),
              answerType: Number(item.answerType),
              materialText: item.materialText,
              score: 0,
              newParentId: item.newParentId,
              showTypeName: item.showTypeName,
              option: item.option,
              analyze: item.analyze,
              answer: item.answer,
              difficultyId: '',
              sortSon: item.sortSon,
              otherTypeName: '',
              showSon: 0,
              belongPage: 0,
              like: item.like,
            });
          });
          rawExamInfo = {
            data: {
              question: question,
              examine: {
                id: 0,
                title: '每日打卡',
                lenTime: 0,
                totalQuestion: question.length,
                totalScore: 0,
                submitLimitNum: '',
                sheetName: '',
                machineType: '',
              },
              progress: {
                answer: [],
                index: '',
                markQuestionIds: [],
                qid: '',
                size: '',
                time: '0',
              },
            },
            eduUserName: '',
            alreadyAnswersNum: '0',
            answersNum: 0,
            isGtOne: false,
            isOnce: false,
            userInfo: {
              mobile: '',
              realName: '',
              sex: '',
              userName: '',
            },
          };

          console.log(rawExamInfo);

          computedExamInfo.value = computeRawExamInfo(rawExamInfo);
        } catch (error) {
          console.error(error);
        }
      }

      return res;
    } else if (examMode.value === ExamModeEnum.ChapterPractice) {
      // 章节练习
      const res = await fetchChapterExamInfo({ id, userSubject }, {}, fetchContext);

      if (res.data.data) {
        try {
          const question: ExamQuestionItem[] = [];
          res.data.data.question.forEach((item, index) => {
            question.push({
              index: index + 1,
              questionId: Number(item.questionId),
              questionTitle: item.questionTitle,
              questionType: Number(item.questionType),
              answerType: Number(item.answerType),
              materialText: item.materialText,
              score: 0,
              newParentId: item.newParentId,
              showTypeName: item.showTypeName,
              option: item.option,
              analyze: item.analyze,
              answer: item.answer,
              userAnswer: getChapterPracticeAnswer(item.userAnswer, item.answer),
              difficultyId: '',
              sortSon: item.sortSon,
              otherTypeName: '',
              showSon: 0,
              belongPage: 0,
              like: item.like,
            });
          });

          examSetting.value.lastQuestionId = +res.data.currentQuestionId;

          rawExamInfo = {
            data: {
              question: question,
              examine: {
                id: +id,
                title: `第${res.data.data.chapterSectionNum.chapterNum}章 第${res.data.data.chapterSectionNum.sectionNum}节 ${res.data.data.title}`,
                lenTime: 0,
                totalQuestion: res.data.data.totalQuestion,
                totalScore: 0,
                submitLimitNum: '',
                sheetName: '',
                machineType: '',
              },
              progress: res.data.progress
                ? res.data.progress
                : {
                    answer: [],
                    index: '',
                    markQuestionIds: [],
                    qid: '',
                    size: '',
                    time: '0',
                  },
            },
            eduUserName: '',
            alreadyAnswersNum: '',
            answersNum: 0,
            isGtOne: false,
            isOnce: false,
            userInfo: {
              mobile: '',
              realName: '',
              sex: '',
              userName: '',
            },
          };
          console.log(rawExamInfo);

          computedExamInfo.value = computeRawExamInfo(rawExamInfo);

          console.log(computedExamInfo.value);
        } catch (error) {
          console.error(error);
        }
      }

      return res;
    } else if (examMode.value === ExamModeEnum.WrongQuestion) {
      // 错题本
      if (
        pageInfo.value.total <= computedExamInfo.value.questionList.length &&
        pageInfo.value.isLoaded
      ) {
        return;
      }

      const res = await fetchUserWrongDetail(
        {
          highFrequency: +(highFrequency || 0),
          userSubject: userSubject + '',
          pageSize: pageInfo.value.pageSize,
          page: pageInfo.value.page,
        },
        {},
        fetchContext
      );
      pageInfo.value.page++;
      pageInfo.value.isLoaded = true;

      pageInfo.value.total = res.data.pagination.total;

      if (res.data.lists) {
        try {
          const question: ExamQuestionItem[] = [];
          res.data.lists.forEach(item => {
            question.push({
              index: item.index,
              questionId: Number(item.questionId),
              questionTitle: item.questionTitle,
              questionType: Number(item.questionType),
              answerType: Number(item.answerType),
              materialText: item.materialText,
              score: 0,
              newParentId: item.newParentId,
              showTypeName: item.showTypeName,
              option: item.option,
              analyze: item.analyze,
              answer: item.answer,
              userAnswer: getQuestionAnswer(
                item.answer,
                examMode.value,
                getQuestionType(+item.questionType, +item.answerType)
              ),
              difficultyId: '',
              sortSon: item.sortSon,
              otherTypeName: '',
              showSon: 0,
              belongPage: 0,
              like: item.like,
            });
          });

          rawExamInfo = {
            data: {
              question: question,
              examine: {
                id: +id,
                title: '错题本练习',
                lenTime: 0,
                totalQuestion: res.data.pagination.total,
                totalScore: 0,
                submitLimitNum: '',
                sheetName: '',
                machineType: '',
              },
              progress: {
                answer: [],
                index: '',
                markQuestionIds: [],
                qid: '',
                size: '',
                time: '0',
              },
            },
            eduUserName: '',
            alreadyAnswersNum: '',
            answersNum: 0,
            isGtOne: false,
            isOnce: false,
            userInfo: {
              mobile: '',
              realName: '',
              sex: '',
              userName: '',
            },
          };
          console.log(rawExamInfo);

          const { questionList, examine, progress } = computeRawExamInfo(rawExamInfo);

          if (aMode === AnswerModeEnum.RecitationMode) {
            questionList.forEach(i => {
              i.isCheck = true;
              i.showAnalyze = false;
            });
          } else {
            questionList.forEach(i => {
              i.isCheck = false;
              i.showAnalyze = false;
            });
          }

          // 获取一下进度
          const userWrongProgressRes = (
            await getUserWrongProgress({ userSubject: userSubject + '' })
          ).data;
          const answerList = needlessAnswer.value ? [] : userWrongProgressRes.lists || [];

          const fullQuestionList = [...computedExamInfo.value.questionList, ...questionList];

          answerList.forEach(i => {
            const index = fullQuestionList.findIndex(j => j.id === i.id);

            if (index > -1) {
              if (
                [ComputedQuestionType.MaterialRichText, ComputedQuestionType.RichText].includes(
                  fullQuestionList[index].computedQuestionType
                )
              ) {
                fullQuestionList[index].answer = getRichTextAnswer(
                  i.answer,
                  i.answerImgs,
                  i.answerText
                );
              } else {
                fullQuestionList[index].answer = i.answer;
              }

              fullQuestionList[index].isCheck = true;

              fullQuestionList[index].disabled = setQuestionDisabled(
                examMode.value,
                answerMode.value,
                isSubjective(fullQuestionList[index].computedQuestionType),
                fullQuestionList[index].answer
              );
            }
          });

          console.log(fullQuestionList);

          computedExamInfo.value = {
            questionList: fullQuestionList,
            examine,
            progress,
          };

          console.log(computedExamInfo.value);
        } catch (error) {
          console.error(error);
        }
      }

      return res;
    } else if (examMode.value === ExamModeEnum.CollectionQuestion) {
      // 收藏夹
      if (
        pageInfo.value.total <= computedExamInfo.value.questionList.length &&
        pageInfo.value.isLoaded
      ) {
        return;
      }

      const res = await fetchCollectionList(
        {
          today: today + '',
          userSubject: pageMeta.value.userSubject!,
          pageSize: pageInfo.value.pageSize,
          page: pageInfo.value.page,
        },
        {},
        fetchContext
      );
      pageInfo.value.page++;
      pageInfo.value.isLoaded = true;

      pageInfo.value.total = res.data.pagination.total;

      if (res.data.lists) {
        try {
          const question: ExamQuestionItem[] = [];
          res.data.lists.forEach(item => {
            question.push({
              index: item.index,
              questionId: Number(item.questionId),
              questionTitle: item.questionTitle,
              questionType: Number(item.questionType),
              answerType: Number(item.answerType),
              materialText: item.materialText,
              score: 0,
              newParentId: item.newParentId,
              showTypeName: item.showTypeName,
              option: item.option,
              analyze: item.analyze,
              answer: item.answer,
              userAnswer: getQuestionAnswer(
                item.answer,
                examMode.value,
                getQuestionType(+item.questionType, +item.answerType)
              ),
              difficultyId: '',
              sortSon: item.sortSon,
              otherTypeName: '',
              showSon: 0,
              belongPage: 0,
              like: item.like,
            });
          });

          rawExamInfo = {
            data: {
              question: question,
              examine: {
                id: +id,
                title: '收藏夹练习',
                lenTime: 0,
                totalQuestion: res.data.pagination.total,
                totalScore: 0,
                submitLimitNum: '',
                sheetName: '',
                machineType: '',
              },
              progress: {
                answer: [],
                index: '',
                markQuestionIds: [],
                qid: '',
                size: '',
                time: '0',
              },
            },
            eduUserName: '',
            alreadyAnswersNum: '',
            answersNum: 0,
            isGtOne: false,
            isOnce: false,
            userInfo: {
              mobile: '',
              realName: '',
              sex: '',
              userName: '',
            },
          };
          console.log(rawExamInfo);

          const { questionList, examine, progress } = computeRawExamInfo(rawExamInfo);

          if (aMode === AnswerModeEnum.RecitationMode) {
            questionList.forEach(i => {
              i.isCheck = true;
              i.showAnalyze = false;
            });
          } else {
            questionList.forEach(i => {
              i.isCheck = false;
              i.showAnalyze = false;
            });
          }

          // 获取一下进度
          const collectionProgressRes = (
            await getCollectionProgress({ userSubject: userSubject + '' })
          ).data;
          const answerList = needlessAnswer.value ? [] : collectionProgressRes.lists || [];

          const fullQuestionList = [...computedExamInfo.value.questionList, ...questionList];

          answerList.forEach(i => {
            const index = fullQuestionList.findIndex(j => j.id === i.id);

            if (index > -1) {
              if (
                [ComputedQuestionType.MaterialRichText, ComputedQuestionType.RichText].includes(
                  fullQuestionList[index].computedQuestionType
                )
              ) {
                fullQuestionList[index].answer = getRichTextAnswer(
                  i.answer,
                  i.answerImgs,
                  i.answerText
                );
              } else {
                fullQuestionList[index].answer = i.answer;
              }

              fullQuestionList[index].isCheck = true;

              fullQuestionList[index].disabled = setQuestionDisabled(
                examMode.value,
                answerMode.value,
                isSubjective(fullQuestionList[index].computedQuestionType),
                fullQuestionList[index].answer
              );
            }
          });

          computedExamInfo.value = {
            questionList: [...computedExamInfo.value.questionList, ...questionList],
            examine,
            progress,
          };

          console.log(computedExamInfo.value);
        } catch (error) {
          console.error(error);
        }
      }

      return res;
    } else if (examMode.value === ExamModeEnum.KnowledgeExam) {
      // 知识点练习
      const res = await fetchKnowledgeExamInfo({ id, userSubject }, {}, fetchContext);

      if (res.data.data) {
        try {
          const question: ExamQuestionItem[] = [];
          res.data.data.question.forEach((item, index) => {
            question.push({
              index: index + 1,
              questionId: Number(item.questionId),
              questionTitle: item.questionTitle,
              questionType: Number(item.questionType),
              answerType: Number(item.answerType),
              materialText: item.materialText,
              score: 0,
              newParentId: item.newParentId,
              showTypeName: item.showTypeName,
              option: item.option,
              analyze: item.analyze,
              answer: item.answer,
              userAnswer: getChapterPracticeAnswer(item.userAnswer, item.answer),
              difficultyId: '',
              sortSon: item.sortSon,
              otherTypeName: '',
              showSon: 0,
              belongPage: 0,
              like: item.like,
            });
          });

          examSetting.value.lastQuestionId = +res.data.currentQuestionId;

          rawExamInfo = {
            data: {
              question: question,
              examine: {
                id: +id,
                title: `${res.data.data.title}`,
                lenTime: 0,
                totalQuestion: res.data.data.totalQuestion,
                totalScore: 0,
                submitLimitNum: '',
                sheetName: '',
                machineType: '',
              },
              progress: res.data.progress
                ? res.data.progress
                : {
                    answer: [],
                    index: '',
                    markQuestionIds: [],
                    qid: '',
                    size: '',
                    time: '0',
                  },
            },
            eduUserName: '',
            alreadyAnswersNum: '',
            answersNum: 0,
            isGtOne: false,
            isOnce: false,
            userInfo: {
              mobile: '',
              realName: '',
              sex: '',
              userName: '',
            },
          };
          console.log(rawExamInfo);

          computedExamInfo.value = computeRawExamInfo(rawExamInfo);

          console.log(computedExamInfo.value);
        } catch (error) {
          console.error(error);
        }
      }

      return res;
    }

    return;
  };

  // 保存答题进度
  const saveAnswerProgress = async (
    startTime: string,
    endTime: string,
    useTime: number,
    version: string,
    username: string
  ) => {
    try {
      const data = {
        id: computedExamInfo.value.examine.id,
        answer: JSON.stringify(examAnswerList.value),
        page: lastAnsweredQuestion.value.page,
        size: '25',
        qid: lastAnsweredQuestion.value.id,
        index: lastAnsweredQuestion.value.qindex,
        totalQuestion: computedExamInfo.value.examine.totalQuestion,
        totalAnswer: answeredQuestionCount.value,
        answerTimeAt: startTime,
        saveTimeAt: endTime,
        appletId: '',
        firstId: '',
        secondId: '',
      };

      if (examMode.value === ExamModeEnum.NormalExam) {
        // 普通考试
        Object.assign(data, {
          diffTime: useTime,
          version,
          markQuestionIds: markedQuestionIds.value,
          boutiqueClassId: '',
          checkChapter: '1',
        });
        const formData = objectToFormData(data);
        const res = await saveExamAnswerProgress(formData);

        return res;
      } else if (examMode.value === ExamModeEnum.ChapterPractice) {
        // 章节练习
        Object.assign(data, {
          userName: username,
          checkChapter: '0',
          time: useTime,
        });
        const formData = objectToFormData(data);

        const res = await saveChapterExamAnswerProgress(formData);

        return res;
      } else if ([ExamModeEnum.ImitateExam, ExamModeEnum.PreviousExam].includes(examMode.value)) {
        // 模拟考场 历年试题
        Object.assign(data, {
          username: username,
          checkChapter: '1',
          time: useTime,
        });
        const formData = objectToFormData(data);

        const res = await saveListExamAnswerProgress(formData);

        return res;
      } else if (examMode.value === ExamModeEnum.WrongQuestion) {
        // 错题本
        const postData = {
          answer: examAnswerList.value.filter(i => i.done),
          userSubject: pageMeta.value.userSubject + '',
        };
        const res = await saveUserWrongProgress(postData);

        return res;
      } else if (examMode.value === ExamModeEnum.CollectionQuestion) {
        // 收藏夹
        const postData = {
          answer: examAnswerList.value.filter(i => i.done),
          userSubject: pageMeta.value.userSubject + '',
        };
        const res = await saveCollectionProgress(postData);

        return res;
      } else if (examMode.value === ExamModeEnum.KnowledgeExam) {
        // 知识点练习
        Object.assign(data, {
          userName: username,
          checkChapter: '0',
          time: useTime,
        });
        const formData = objectToFormData(data);

        const res = await saveKnowledgeExamAnswerProgress(formData);

        return res;
      }

      return;
    } catch (error) {
      console.error(error);
    }
  };

  // 提交答案
  const submitAnswer = async (
    time: number,
    autoSubmit: number,
    version: string,
    router: Router
  ) => {
    try {
      const MAX_COUNT_UP_TIME = 3600 * 6;

      if (computedExamInfo.value.examine.lenTime === 0) {
        // 正计时
        time = time > MAX_COUNT_UP_TIME ? MAX_COUNT_UP_TIME : time;
      } else {
        // 倒计时
      }

      const data = {
        answer: JSON.stringify(examAnswerList.value),
        userSubject: pageMeta.value.userSubject,
      };

      if (examMode.value === ExamModeEnum.NormalExam) {
        Object.assign(data, {
          id: computedExamInfo.value.examine.id,
          version,
          markQuestionIds: markedQuestionIds.value,
          autoSubmit: autoSubmit,
          useTime: time,
        });

        if (isCertExam.value) {
          Object.assign(data, {
            tradeNumber: certExamInfo.value.tradeNumber,
            exchangeType: certExamInfo.value.exchangeType,
            codeId: certExamInfo.value.codeId,
          });
        }

        const formData = objectToFormData(data);
        const res = await submitExamAnswer(formData);

        if (isCertExam.value) {
          return `${GLOBAL_CONFIG.envConfig.origin}/cert/cert-detail?exam_id=${certExamInfo.value.examId}&code_id=${certExamInfo.value.codeId}&trade_number=${certExamInfo.value.tradeNumber}&exchange_type=${certExamInfo.value.exchangeType}`;
        } else {
          const resultPageRoute = generateExamResultUrl(router, {
            mode: examMode.value,
            answerId: res.data.answerId,
            answerMode: answerMode.value,
            autoShowPoster: 'true',
          });

          return resultPageRoute;
        }
      } else if ([ExamModeEnum.ImitateExam, ExamModeEnum.PreviousExam].includes(examMode.value)) {
        Object.assign(data, {
          id: computedExamInfo.value.examine.id,
          area: ExamModeEnum.ImitateExam === examMode.value ? '4' : '3',
          useTime: time,
        });
        const formData = objectToFormData(data);
        const res = await submitListExamAnswer(formData);

        const resultPageRoute = generateExamResultUrl(router, {
          mode: examMode.value,
          answerId: computedExamInfo.value.examine.id + '',
          answerMode: answerMode.value,
        });

        return resultPageRoute;
      } else if (examMode.value === ExamModeEnum.PunchInDaily) {
        // 每日打卡
        Object.assign(data, {
          date: pageMeta.value.date,
          examTime: time,
        });
        const formData = objectToFormData(data);
        await submitDailyPunchCardAnswer(formData);

        const baseParams: {
          mode: ExamModeEnum.PunchInDaily;
          date: string;
          id?: string;
          userSubject?: string;
        } = {
          mode: examMode.value,
          date: pageMeta.value.date!,
        };

        // 根据条件添加userSubject (应后端要求不需要id)
        if (pageMeta.value.userSubject) {
          baseParams.userSubject = pageMeta.value.userSubject;
        }

        const resultPageRoute = generateExamResultUrl(router, baseParams);

        return resultPageRoute;
      } else if (examMode.value === ExamModeEnum.ChapterPractice) {
        // 章节练习
        Object.assign(data, {
          id: computedExamInfo.value.examine.id,
          examTime: time,
          currentQuestionId:
            computedExamInfo.value.questionList[examSetting.value.currentQuestionIndex].id,
        });
        const formData = objectToFormData(data);
        const res = await submitChapterExamAnswer(formData);

        const resultPageRoute = generateExamResultUrl(router, {
          mode: examMode.value,
          submitId: res.data.submitId,
          id: computedExamInfo.value.examine.id + '',
          answerMode: answerMode.value,
        });

        return resultPageRoute;
      } else if (examMode.value === ExamModeEnum.WrongQuestion) {
        // 错题本
        const answer: UserWrongAnswerItem[] = [];
        await submitUserWrong(
          { answer: JSON.stringify(examAnswerList.value.filter(i => i.done)), examTime: time },
          { isSkipConvertRequest: true }
        );

        const resultPageRoute = generateExamResultUrl(router, {
          mode: examMode.value,
          answerMode: answerMode.value,
          userSubject: pageMeta.value.userSubject,
          highFrequency: pageMeta.value.highFrequency,
        });

        return resultPageRoute;
      } else if (examMode.value === ExamModeEnum.CollectionQuestion) {
        // 收藏夹
        const answer: UserCollectionAnswerItem[] = [];
        await submitUserCorrection(
          {
            answer: JSON.stringify(examAnswerList.value.filter(i => i.done)),
            userSubject: pageMeta.value.userSubject + '',
            examTime: time,
          },
          { isSkipConvertRequest: true }
        );
        const resultPageRoute = generateExamResultUrl(router, {
          mode: examMode.value,
          answerMode: answerMode.value,
          userSubject: pageMeta.value.userSubject,
          highFrequency: pageMeta.value.highFrequency,
          today: pageMeta.value.today,
        });

        return resultPageRoute;
      } else if (examMode.value === ExamModeEnum.KnowledgeExam) {
        // 知识点答题
        Object.assign(data, {
          id: computedExamInfo.value.examine.id,
          examTime: time,
          userSubject: pageMeta.value.userSubject + '',
          currentQuestionId:
            computedExamInfo.value.questionList[examSetting.value.currentQuestionIndex].id,
        });
        const formData = objectToFormData(data);
        const res = await submitKnowledgeExamAnswer(formData);
        console.log(res);
        const resultPageRoute = generateExamResultUrl(router, {
          mode: examMode.value,
          submitId: res.data.submitId,
          id: computedExamInfo.value.examine.id + '',
          answerMode: answerMode.value,
        });

        return resultPageRoute;
      }

      return;
    } catch (error) {
      console.error(error);
    }
  };

  // 更新题目收藏状态
  const updateQuestionCollectStatus = async (
    question: ComputedExamQuestionItem,
    isMobile: boolean
  ) => {
    const { id, like } = question;
    let loading;

    if (isMobile) {
      loading = showLoadingToast({
        message: '加载中...',
        forbidClick: true,
      });
    } else {
      loading = ElLoading.service({
        lock: true,
        text: '加载中...',
        background: 'rgba(0, 0, 0, 0)',
      });
    }

    const collectQuestionData = new FormData();
    collectQuestionData.append('qid', id + '');

    try {
      if (like === QuestionCollectStatus.Like) {
        await removeCollectQuestion(collectQuestionData);
        question.like = QuestionCollectStatus.Unlike;

        if (isMobile) {
          showSuccessToast('已取消收藏');
        } else {
          ElMessage.success('已取消收藏');
        }
      } else if (like === QuestionCollectStatus.Unlike) {
        await addCollectQuestion(collectQuestionData);
        question.like = QuestionCollectStatus.Like;

        if (isMobile) {
          showSuccessToast('已收藏');
        } else {
          ElMessage.success('已收藏');
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      loading?.close();
    }
  };

  const reset = () => {
    resetPageMeta();
    resetExamInfo();
    examMode.value = 1;
    answerMode.value = 1;
  };

  return {
    pageMeta,
    updateExamInfo,
    saveAnswerProgress,
    submitAnswer,
    updateQuestionCollectStatus,
    examMode,
    answerMode,
    computedExamInfo,
    answeredQuestionCount,
    markedQuestionIds,
    examAnswerList,
    lastAnsweredQuestion,
    examSetting,
    examStartTips,
    examTimeEndTips,
    isCertExam,
    pageInfo,
    needlessAnswer,
    // store reset
    reset,
  };
});
